// Turns an Axios error into a precise, user-facing explanation instead of
// Axios' generic "Request failed with status code 403".
//
// The status code alone is ambiguous on this backend:
//  - 401: no token at all, or the refresh token is dead.
//  - 403 "Invalid or expired token." (verifyAnyToken, used by GET /products):
//    a token was sent but the server refuses it (expired, or issued by another
//    server) - fix is to sign in again.
//  - 403 "Access denied..." (requirePermission): the token is fine, the
//    account's role just lacks the permission - signing in again won't help.
export const describeApiError = (err, fallbackTitle = "Erreur de chargement") => {
  const response = err?.response;
  const status = response?.status ?? null;
  const serverMessage = response?.data?.message || response?.data?.error || "";
  const detail = status
    ? `HTTP ${status}${serverMessage ? ` - ${serverMessage}` : ""}`
    : err?.message || "";

  if (!response) {
    return {
      status,
      isAuthError: false,
      title: "Serveur injoignable",
      message: "Impossible de contacter le serveur. Vérifiez votre connexion, puis réessayez.",
      detail,
    };
  }

  const looksLikeTokenProblem = /token|expired|session/i.test(serverMessage);
  if (status === 401 || (status === 403 && looksLikeTokenProblem)) {
    return {
      status,
      isAuthError: true,
      title: "Session expirée ou invalide",
      message:
        "Votre connexion n'est plus valide : le serveur a refusé votre jeton (expiré ou délivré par un autre serveur). Déconnectez-vous puis reconnectez-vous.",
      detail,
    };
  }

  if (status === 403) {
    return {
      status,
      isAuthError: false,
      title: "Accès refusé",
      message:
        "Vous êtes bien connecté, mais votre compte n'a pas la permission d'accéder à cette ressource. Demandez à un administrateur d'ajuster votre rôle.",
      detail,
    };
  }

  if (status >= 500) {
    return {
      status,
      isAuthError: false,
      title: "Erreur du serveur",
      message: "Le serveur a rencontré un problème. Réessayez dans un instant.",
      detail,
    };
  }

  return {
    status,
    isAuthError: false,
    title: fallbackTitle,
    message: serverMessage || err?.message || "Une erreur est survenue.",
    detail,
  };
};
